export type BeforeAfterImageSource = string | HTMLCanvasElement;

export type BeforeAfterLayout = 'landscape' | 'portrait';

export interface BeforeAfterCardOptions {
  // Several sources are tiled into the before panel (e.g. combine-mode inputs)
  before: BeforeAfterImageSource | BeforeAfterImageSource[];
  after: BeforeAfterImageSource;
  // Shown as "Apps > {appName}", matching the site's Apps menu
  appName: string;
  layout: BeforeAfterLayout;
}

interface LayoutSpec {
  width: number;
  height: number;
  padding: number;
  gap: number;
  // Type is sized for a phone feed, where the card is shown ~360px wide
  type: { wordmark: number; appPath: number; url: number; pill: number };
  footerHeight: number;
  arrowRadius: number;
}

const LAYOUTS: Record<BeforeAfterLayout, LayoutSpec> = {
  // 16:9 for X, LinkedIn and Facebook feeds
  landscape: {
    width: 1600, height: 900, padding: 56, gap: 40,
    type: { wordmark: 38, appPath: 31, url: 32, pill: 28 },
    footerHeight: 90, arrowRadius: 48,
  },
  // 4:5 for Instagram and Pinterest
  portrait: {
    width: 1080, height: 1350, padding: 48, gap: 36,
    type: { wordmark: 36, appPath: 30, url: 30, pill: 24 },
    footerHeight: 88, arrowRadius: 42,
  },
};

const FOOTER_GAP = 32;
const ARROW_COLOR = '#16A34A';
const PANEL_RADIUS = 24;
const PANEL_INSET = 24;
const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Each code needs a matching /try/ redirect in netlify.toml, which adds the UTM
// tags server-side so first-touch referral tracking sees them on the first load
export type ShareCode = 'lifestyle' | 'studio' | 'listing' | 'remove-bg' | 'photo-retouch';

export function buildShortShareUrl(code: ShareCode): string {
  return `merchantfloor.com/try/${code}`;
}

function getBrandColor(): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--color-primary-600').trim();
  return value || '#0D9488';
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Blob and data URLs are same-origin; remote URLs need CORS so the card can be exported
    if (!src.startsWith('blob:') && !src.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for share card'));
    img.src = src;
  });
}

async function resolveSource(source: BeforeAfterImageSource): Promise<CanvasImageSource & { width: number; height: number }> {
  return typeof source === 'string' ? loadImage(source) : source;
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawContained(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource & { width: number; height: number },
  x: number,
  y: number,
  w: number,
  h: number
) {
  const scale = Math.min(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

function drawPanel(
  ctx: CanvasRenderingContext2D,
  images: (CanvasImageSource & { width: number; height: number })[],
  x: number,
  y: number,
  w: number,
  h: number
) {
  ctx.save();
  ctx.shadowColor = 'rgba(28, 25, 23, 0.08)';
  ctx.shadowBlur = 24;
  ctx.shadowOffsetY = 6;
  roundedRectPath(ctx, x, y, w, h, PANEL_RADIUS);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.restore();

  ctx.save();
  roundedRectPath(ctx, x, y, w, h, PANEL_RADIUS);
  ctx.clip();

  const innerX = x + PANEL_INSET;
  const innerY = y + PANEL_INSET;
  const innerW = w - PANEL_INSET * 2;
  const innerH = h - PANEL_INSET * 2;

  if (images.length === 1) {
    drawContained(ctx, images[0], innerX, innerY, innerW, innerH);
  } else {
    const tiles = images.slice(0, 4);
    const cols = 2;
    const rows = Math.ceil(tiles.length / cols);
    const tileGap = 12;
    const tileW = (innerW - tileGap * (cols - 1)) / cols;
    const tileH = (innerH - tileGap * (rows - 1)) / rows;
    tiles.forEach((img, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      drawContained(ctx, img, innerX + col * (tileW + tileGap), innerY + row * (tileH + tileGap), tileW, tileH);
    });
  }
  ctx.restore();
}

function drawPill(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, fontSize: number, background: string) {
  ctx.save();
  ctx.font = `700 ${fontSize}px ${FONT}`;
  (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${Math.round(fontSize * 0.1)}px`;
  const textWidth = ctx.measureText(text).width;
  const padX = Math.round(fontSize * 0.8);
  const h = Math.round(fontSize * 1.9);
  roundedRectPath(ctx, x, y, textWidth + padX * 2, h, h / 2);
  ctx.fillStyle = background;
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, y + h / 2 + 1);
  ctx.restore();
}

function drawArrowBadge(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, direction: 'right' | 'down') {
  ctx.save();
  ctx.shadowColor = 'rgba(22, 163, 74, 0.35)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = ARROW_COLOR;
  ctx.fill();
  ctx.restore();

  // White ring separates the badge from the panels it overlaps
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 2, 0, Math.PI * 2);
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.restore();

  const s = r / 30;
  ctx.save();
  ctx.translate(cx, cy);
  if (direction === 'down') ctx.rotate(Math.PI / 2);
  ctx.scale(s, s);
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(-11, 0);
  ctx.lineTo(11, 0);
  ctx.moveTo(1, -10);
  ctx.lineTo(11, 0);
  ctx.lineTo(1, 10);
  ctx.stroke();
  ctx.restore();
}

function drawFooter(
  ctx: CanvasRenderingContext2D,
  spec: LayoutSpec,
  appName: string,
  x: number,
  y: number,
  w: number,
  brand: string
) {
  const { type, footerHeight } = spec;
  const midY = y + footerHeight / 2;

  // Two lines: wordmark with the URL beside it, then the app path at full width
  const blockHeight = type.wordmark * 1.25 + type.appPath;
  const top = midY - blockHeight / 2;
  const line1Y = top + type.wordmark / 2;
  const line2Y = top + type.wordmark * 1.25 + type.appPath / 2;

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1C1917';
  ctx.font = `700 ${type.wordmark}px ${FONT}`;
  ctx.fillText('MerchantFloor', x, line1Y);

  ctx.textAlign = 'right';
  ctx.fillStyle = brand;
  ctx.font = `600 ${type.url}px ${FONT}`;
  ctx.fillText('merchantfloor.com', x + w, line1Y);

  // Long app names shrink rather than overflow the card
  const appPath = `Apps > ${appName}`;
  const maxWidth = w;
  let appPathSize = type.appPath;
  ctx.textAlign = 'left';
  ctx.font = `500 ${appPathSize}px ${FONT}`;
  while (appPathSize > 18 && ctx.measureText(appPath).width > maxWidth) {
    appPathSize -= 1;
    ctx.font = `500 ${appPathSize}px ${FONT}`;
  }
  ctx.fillStyle = '#57534E';
  ctx.fillText(appPath, x, line2Y);
}

export async function renderBeforeAfterCard(options: BeforeAfterCardOptions): Promise<HTMLCanvasElement> {
  const spec = LAYOUTS[options.layout];
  const { width, height, padding, gap, type, footerHeight } = spec;
  const beforeSources = Array.isArray(options.before) ? options.before : [options.before];

  await Promise.all([
    document.fonts.load(`700 ${type.wordmark}px Inter`),
    document.fonts.load(`600 ${type.url}px Inter`),
    document.fonts.load(`500 ${type.appPath}px Inter`),
  ]).catch(() => undefined);

  const [beforeImages, afterImage] = await Promise.all([
    Promise.all(beforeSources.map(resolveSource)),
    resolveSource(options.after),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser');

  const brand = getBrandColor();

  ctx.fillStyle = '#F5F5F4';
  ctx.fillRect(0, 0, width, height);

  const contentW = width - padding * 2;
  const panelsH = height - padding * 2 - footerHeight - FOOTER_GAP;

  let before: { x: number; y: number; w: number; h: number };
  let after: { x: number; y: number; w: number; h: number };

  if (options.layout === 'landscape') {
    const panelW = (contentW - gap) / 2;
    before = { x: padding, y: padding, w: panelW, h: panelsH };
    after = { x: padding + panelW + gap, y: padding, w: panelW, h: panelsH };
  } else {
    const panelH = (panelsH - gap) / 2;
    before = { x: padding, y: padding, w: contentW, h: panelH };
    after = { x: padding, y: padding + panelH + gap, w: contentW, h: panelH };
  }

  drawPanel(ctx, beforeImages, before.x, before.y, before.w, before.h);
  drawPanel(ctx, [afterImage], after.x, after.y, after.w, after.h);

  drawPill(ctx, 'BEFORE', before.x + 20, before.y + 20, type.pill, 'rgba(28, 25, 23, 0.82)');
  drawPill(ctx, 'AFTER', after.x + 20, after.y + 20, type.pill, brand);

  if (options.layout === 'landscape') {
    drawArrowBadge(ctx, padding + before.w + gap / 2, before.y + before.h / 2, spec.arrowRadius, 'right');
  } else {
    drawArrowBadge(ctx, width / 2, before.y + before.h + gap / 2, spec.arrowRadius, 'down');
  }

  drawFooter(ctx, spec, options.appName, padding, height - padding - footerHeight, contentW, brand);

  return canvas;
}

export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to export share card'));
      }, 'image/png');
    } catch (err) {
      // A cross-origin image without CORS headers taints the canvas
      reject(err instanceof Error ? err : new Error('Failed to export share card'));
    }
  });
}
